import '../styles/main.scss';

import React, { useContext, useRef } from 'react';

import { graphql, useStaticQuery } from 'gatsby';
import { useTranslation } from 'react-i18next';
import ModulesContent, { Module } from '../components/ModulesContent';
import PageSEO, { PageSEOProps } from '../components/PageSEO';
import StructuredData from '../components/StructuredData';
import Hero, { HeroType } from '../components/ui/Hero';
import FormModule from '../components/ui/modules/FormModule';
import SanityLayout, { CommonSettings } from '../components/ui/SanityLayout';
import { ALL_LANGS } from '../constants';
import { LocationContext } from '../contexts/LocationContext';
import { LocalizedSEO } from '../graphql-fragments/seo';
import { getLocalizedUrlPath } from '../utils/i18n';
import { localize } from '../utils/sanity';

interface PageTemplateProps {
  page: {
    title: string;
    slug: {
      current: string;
    };
    hero: HeroType;
    modules?: Array<Module>;
    hideFormModule?: boolean;
    seo?: LocalizedSEO;
  };
  description?: string;
  hardCodedModules?: React.ReactNode;
  hideNavigation?: boolean;
  defaultSEO?: PageSEOProps;
  // Page-specific JSON-LD (e.g. Service).
  structuredData?: React.ReactNode;
}

interface LocalizedQueryData {
  sanityWebsite: CommonSettings;
}

const PageTemplate = ({
  page,
  description,
  hardCodedModules,
  hideNavigation,
  defaultSEO,
  structuredData,
}: PageTemplateProps): React.ReactElement => {
  const staticData = useStaticQuery<LocalizedQueryData>(graphql`
    {
      sanityWebsite {
        contacts {
          calendlyLink
        }
      }
    }
  `);

  const { i18n, t } = useTranslation();

  const langs = [i18n.language, ...ALL_LANGS.filter(lang => lang !== i18n.language)];
  const localizedData: LocalizedQueryData = localize(staticData, langs);

  const { title, hero, modules, seo } = page;

  const { sanityWebsite } = localizedData;

  const heroRef = useRef<HTMLElement | null>(null);

  const location = useContext(LocationContext);

  const isHomepage = location?.pathname === '/';

  // Unlike `isHomepage`, this also matches the EN homepage (`/en/`).
  const homepageLang = ALL_LANGS.find(
    lang => location?.pathname === getLocalizedUrlPath('/', lang),
  );

  return (
    <SanityLayout hideNavigation={hideNavigation} heroRef={heroRef}>
      <PageSEO
        pageSEO={seo}
        defaultDescription={isHomepage ? '' : defaultSEO?.defaultDescription}
        defaultImageUrl={isHomepage ? '' : defaultSEO?.defaultImageUrl}
        defaultTitle={isHomepage ? '' : defaultSEO?.defaultTitle}
      />
      {homepageLang && <StructuredData lang={homepageLang} />}
      {structuredData}
      <Hero
        {...hero}
        title={hero?.title || title}
        text={hero?.text || description}
        heroRef={heroRef}
        calendlyLink={sanityWebsite.contacts.calendlyLink}
        isFullHeight={isHomepage}
        withAnimations={isHomepage}
      />
      {modules && modules.length > 0 && (
        <ModulesContent modules={modules} calendlyLink={sanityWebsite.contacts.calendlyLink} />
      )}
      {hardCodedModules}
      {!page.hideFormModule && <FormModule />}
    </SanityLayout>
  );
};

export default PageTemplate;
